import React, { useState, useCallback, memo } from 'react';
import { EditorState, SelectionState, ContentState } from 'draft-js';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';

import SidePanelTextEditor from 'Dashboard/components/SidePanelTextEditor/SidePanelTextEditor';
import { editorStateToJsonString, isEmptyEditor, jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';

import { createRichTextEditorState } from 'Wpds/components/rich-text/utils/EditorState.utils';
import { modifyUrlBySmartLink } from 'Shared/components/DraftEditor/plugins/smartLinksPlugin';
import { useCreateWorkspaceComment } from 'Shared/actions/workspaceComments';
import { setOptionsForInviteToWorkspaceModal } from 'Shared/actions/optionsForInviteToWorkspaceModal';
import { enrichEditorStateByImageAttachment } from 'Shared/components/DraftEditor/plugins/imageAttachmentsPlugin';
import {
  enrichEditorStateByGif,
  updateEntitysOfGifBeforeSend,
} from 'Shared/components/DraftEditor/plugins/addGifPlugin';
import { enrichEditorStateByEmoji } from 'Shared/components/DraftEditor/plugins/addEmojiPlugin';

import './CommentTextEditor.scss';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  showSaveButton?: boolean;
  toolbarPlacement?: 'top' | 'bottom';
  showHint?: boolean;
  placeholder?: string;
  meetingItem?: {
    id?: number;
    uuid?: string;
  };
  workspaceId?: string;
  handleCreateComment?: Function;
  hideVideo?: boolean;
  autoFocus?: boolean;
  outerEditorState?: EditorState;
  onChange?: Function;
  enterToSend?: boolean;
  isSaving?: boolean;
  showGif?: boolean;
  showEmoji?: boolean;
  showAttachment?: boolean;
  enableMentions?: boolean;
};

function CommentTextEditor({
  showSaveButton = false,
  toolbarPlacement,
  showHint = true,
  placeholder,
  meetingItem,
  handleCreateComment,
  hideVideo,
  autoFocus,
  outerEditorState,
  onChange,
  enterToSend,
  isSaving,
  workspaceId,
  showGif,
  showEmoji,
  showAttachment,
  enableMentions,
}: Props) {
  const dispatch = useDispatch();
  const usersFromWorkspace = useSelector(state => state.workspaces.find(({ id }) => id === workspaceId)?.users || []);
  const initEditorState = createRichTextEditorState(outerEditorState);
  const [editorState, setEditorState] = useState(initEditorState);

  const [createWorkspaceComment] = useCreateWorkspaceComment({ meetingItem });

  // useEffect(() => {
  //   if (outerEditorState) setEditorState(createRichTextEditorState(outerEditorState));
  // }, [outerEditorState]);

  const setState = useCallback(
    state => {
      if (typeof state === 'function') {
        setEditorState(state({ editorState }).editorState);
      } else {
        setEditorState(state.editorState);
      }
    },
    [editorState]
  );

  const handleEditorChange = useCallback(
    editorStateValue => {
      if (onChange) {
        onChange(editorStateValue);
      }
      // else {
      modifyUrlBySmartLink(editorStateValue, { setState });
      // }
    },
    [setState, onChange]
  );

  const handleCommentCreateClick = useCallback(async () => {
    if (isEmptyEditor(editorState)) return;
    const body = editorStateToJsonString(updateEntitysOfGifBeforeSend(editorState));

    let newEditorState = EditorState.push(editorState, ContentState.createFromText(''));
    const key = newEditorState.getCurrentContent().getBlockMap().first().getKey();
    const selectionState = SelectionState.createEmpty(key);
    newEditorState = EditorState.forceSelection(newEditorState, selectionState);
    handleEditorChange(newEditorState);

    if (handleCreateComment) {
      handleCreateComment({ body });
    } else {
      await createWorkspaceComment({ body });
    }
  }, [editorState, createWorkspaceComment]);

  const handleAttachmentInputChange = useCallback(
    async file => {
      const state = await enrichEditorStateByImageAttachment(editorState, file);
      handleEditorChange(state);
    },
    [editorState]
  );

  const handleGifAdd = useCallback(gif => handleEditorChange(enrichEditorStateByGif(editorState, gif)), [editorState]);

  const handleEmojiAdd = useCallback(
    emoji => handleEditorChange(enrichEditorStateByEmoji(editorState, emoji)),
    [editorState]
  );

  const handleCreateVideoComment = useCallback(
    video => {
      const body = jsonContentStateFromString(video.audioOnly ? 'New Audio Item' : 'New Video Item');
      if (handleCreateComment) {
        handleCreateComment({ body, video });
      } else {
        return createWorkspaceComment({ body, video });
      }
    },
    [createWorkspaceComment, handleCreateComment]
  );

  const handleAssignedPersonAdd = useCallback(
    value => {
      if (value && !usersFromWorkspace.find(({ personId }) => personId === value.id))
        dispatch(setOptionsForInviteToWorkspaceModal({ mentionedNewEmail: value.email, workspaceId: workspaceId }));
    },
    [dispatch, workspaceId, usersFromWorkspace]
  );

  const { tr } = useTranslation();
  return (
    <>
      <SidePanelTextEditor
        showSaveButton={showSaveButton}
        isSaving={isSaving}
        toolbarPlacement={toolbarPlacement}
        placeholder={placeholder}
        meetingItemUuid={meetingItem?.uuid}
        editorState={editorState}
        onEditorChange={handleEditorChange}
        onAttachmentInputChange={handleAttachmentInputChange}
        onSaveVideo={handleCreateVideoComment}
        onAssignedPersonAdd={handleAssignedPersonAdd}
        onSubmit={handleCommentCreateClick}
        onGifAdd={handleGifAdd}
        onEmojiAdd={handleEmojiAdd}
        hideVideo={hideVideo}
        autoFocus={autoFocus}
        enterToSend={enterToSend}
        workspaceId={workspaceId}
        showGif={showGif}
        showEmoji={showEmoji}
        showAttachment={showAttachment}
        enableMentions={enableMentions}
        hint={
          showHint && (
            <div className="comment-text-editor-hint d-flex justify-content-end py-1">
              <div className="mr-2">
                <span>{tr('perform.common.keyboard.return')}</span> {tr('perform.common.editor.hint.send_lower')}
              </div>
              <div>
                <span>{tr('perform.common.keyboard.shift_return')}</span> {tr('perform.common.editor.hint.new_line')}
              </div>
            </div>
          )
        }
      />
    </>
  );
}

export default memo(CommentTextEditor, isEqual);
